/**
 * Work Order Service
 *
 * Provides CRUD operations and sub-resource management for work orders — the
 * core unit of work tracked by the platform. Work orders represent jobs assigned
 * to technicians at specific locations, with billing, scheduling, photos, time
 * logs, and status tracking.
 *
 * Backend endpoints:
 *	 GET	/api/work-orders						— List/filter work orders
 *	 GET	/api/work-orders/:id					— Get a single work order
 *	 POST	/api/work-orders						— Create a new work order
 *	 PATCH	/api/work-orders/:id					— Update an existing work order
 *	 DELETE /api/work-orders/:id					— Soft-delete a work order
 *	 POST	/api/work-orders/:id/time-logs			— Add a time log entry
 *	 POST	/api/work-orders/:id/photos				— Add a photo
 *	 DELETE /api/work-orders/:id/photos/:photoId	— Delete a photo
 *	 GET	/api/work-orders/:id/pdf?type=			— Generate PDF report
 *
 * All endpoints require authentication (Sanctum Bearer token).
 */

import { api } from './api';
import type { NotifyDecision } from './notificationService';
import { T_AND_M_SUB_TYPE } from '../utils/constants';
import type { WorkOrderStatus, BillingType } from '../utils/constants';

/**
 * Business Line is now a free-form slug pulled from the CMS-managed
 * business_lines table at runtime — see businessLineService. Until the
 * 2026-05-24 refactor the value was constrained to a hardcoded union,
 * which doesn't match the new "ops can add new lines without a deploy"
 * data model. Keep it loose here.
 */
export type BusinessLineSlug = string;

/** Sub Type slug — same story as BusinessLineSlug. The 't_and_m'
 *  value remains a system slug because business rules branch on it. */
export type SubTypeSlug = string;

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Represents a photo attached to a work order (before/after images).
 *
 * @property id			- Unique photo identifier (UUID)
 * @property url		- Full URL to the stored image file
 * @property caption	- Optional description of what the photo shows
 * @property photoType	- Categorization: 'before' or 'after' the work
 * @property createdAt	- ISO timestamp of when the photo was uploaded
 */
export interface WorkOrderPhoto {
	id: string;
	url: string;
	caption: string;
	photoType: 'before' | 'after';
	createdAt: string;
}

/**
 * Represents a time log entry for tracking labor hours on a work order.
 *
 * @property id			- Unique time log identifier (UUID)
 * @property employeeName - Display name of the employee who logged time
 * @property employeeId - UUID of the employee user
 * @property date		- ISO date string for the day work was performed
 * @property timeIn		- ISO datetime of clock-in
 * @property timeOut	- ISO datetime of clock-out
 * @property hours		- Calculated total hours worked (decimal)
 * @property notes		- Optional notes about the work performed during this period
 * @property createdAt	- ISO timestamp of when this entry was created
 */
export interface TimeLog {
	id: string;
	employeeName: string;
	employeeId: string;
	date: string;
	timeIn: string;
	timeOut: string;
	hours: number;
	notes: string;
	createdAt: string;
}

/**
 * Represents a work order as returned by the API.
 *
 * @property id				   - Unique identifier (UUID)
 * @property projectName	   - Short descriptive title for the work order
 * @property description	   - Detailed description of the work to be performed
 * @property status			   - Current lifecycle status (see WorkOrderStatus type)
 * @property billingType	   - How this work order is priced (fixed, T&M, NTE, unit)
 * @property businessLine	   - Service category (janitorial, construction, etc.)
 * @property subType           - CMS-managed sub-type slug under the selected business line, or null
 * @property locationId		   - The location where the work is performed
 * @property locationName	   - Display name of the location (eager-loaded from relation)
 * @property assignedToId	   - The technician assigned to this work order, or null if unassigned
 * @property assigneeName	   - Display name of the assignee (eager-loaded from relation)
 * @property companyId		   - The customer company this work order belongs to
 * @property companyName	   - Display name of the company (eager-loaded from relation)
 * @property contactName	   - On-site contact person name
 * @property contactPhone	   - On-site contact phone number
 * @property contactEmail	   - On-site contact email address
 * @property projectAddress	   - Physical address where work is performed
 * @property priority		   - Priority level: 'low', 'medium', 'high', 'urgent'
 * @property scheduledDate	   - ISO date when work is scheduled
 * @property billingInfo	   - Array of billing line item categories
 * @property billingName	   - Name for billing/invoicing purposes
 * @property billingAddress	   - Address for billing/invoicing purposes
 * @property billingNotes	   - Additional billing notes
 * @property billingAmount	   - Total billing amount in dollars
 * @property photosRequired	   - Whether before/after photos are required
 * @property signatureRequired - Whether a customer signature is required on completion
 * @property photos			   - Array of attached photos (eager-loaded)
 * @property timeLogs		   - Array of time log entries (eager-loaded)
 * @property createdAt		   - ISO timestamp of when the work order was created
 * @property updatedAt		   - ISO timestamp of the last modification
 */
/**
 * A person assigned to a work order, as returned on the eager-loaded
 * `assignees` relation. The pivot carries which one is primary and, on
 * hour-tracked jobs, the hours allocated to that person.
 */
export interface WorkOrderAssignee {
	id: string | number;
	firstName?: string;
	lastName?: string;
	role?: string | null;
	pivot?: {
		isPrimary?: boolean | number;
		allocatedHours?: string | number | null;
	};
}

export interface WorkOrder {
	id: string;
	projectName: string;
	description: string;
	status: WorkOrderStatus;
	billingType: BillingType;
	businessLine: BusinessLineSlug;
	/** CMS-managed Sub Type slug — replaces the old constructionType +
	 *  janitorialType pair. Null when the line has no sub-type selected. */
	subType: SubTypeSlug | null;
	/** Whether this work order tracks hours — "Require Hours" was ticked,
	 *  or its sub-type is T&M, which forces it on. When true every
	 *  assignee carries hours and the record can't be submitted or
	 *  completed until they're recorded. */
	requiresHours: boolean;
	/** Bucket of hours this work order draws from
	 *  (work_orders.allocated_hours). Contractual on T&M, optional
	 *  elsewhere. Also the field a child WO of a Project uses for its
	 *  slice of the parent's pool — one column, one meaning. */
	allocatedHours: string | number | null;
	/** The job's ACTUAL hours (work_orders.total_hours): the sum of the
	 *  crew's per-person hours, rolled up server-side whenever they are
	 *  written. Read beside allocatedHours, the estimate. */
	totalHours?: string | number | null;
	/** When the recorded hours were handed off for approval (stamped by
	 *  the submit/sign-off that declares the work finished). */
	hoursSubmittedAt: string | null;
	hoursSubmittedBy?: { id: string; firstName: string; lastName: string } | null;
	/** When the hours were signed off. Until this is set, an hours-tracked
	 *  work order can't move into the invoiced/closed billing tail. */
	hoursApprovedAt: string | null;
	/** Our-side approver (admin / super admin). Exactly one of these two
	 *  approver fields is set per approval. */
	hoursApprovedByUser?: { id: string; firstName: string; lastName: string } | null;
	/** Customer-side approver — the work order's primary contact. */
	hoursApprovedByContact?: { id: string; firstName: string; lastName: string } | null;
	locationId: string;
	locationName: string;
	assignedToId: string | null;
	/** Primary assignee's user id (flattened from the assignee relation /
	 *  assignee_id). Used to detect when the on-site contact IS the assignee. */
	assigneeId: string | null;
	assigneeName: string | null;
	/** Role of the primary assignee (eager-loaded from the assignee relation).
	 *  Surfaces 'subcontractor' so the detail page can render a vendor badge. */
	assigneeRole?: string | null;
	companyId: string;
	companyName: string;
	contactName: string;
	contactPhone: string;
	contactEmail: string;
	/**
	 * When the on-site contact was last emailed to verify + sign, or null if
	 * they never were (the technician answered "Don't Email" at submit time,
	 * or no address is on file). Written only by the server; the detail page
	 * reads it to choose between "Email Contact to Verify" and "Resend
	 * Verification Email".
	 */
	contactNotifiedAt?: string | null;
	/** FK to the linked Customer Contact record (contacts.id). Null when the
	 *  WO only carries the denormalized name/phone/email snapshot. Drives
	 *  edit-mode hydration of the contact picker. */
	contactId: string | null;
	/** Linked user id of the on-site contact (via the contact_users pivot),
	 *  when the contact is also a platform user. Null when the contact is a
	 *  plain customer rep. Compared against assigneeId to decide whether the
	 *  in-app Verify + Sign Off steps apply (they only do when the assignee
	 *  is also the on-site contact). */
	contactUserId: string | null;
	projectAddress: string;
	/** Remaining site-address parts. Stored as their own columns so the
	 *  address can be handed to a maps app whole — the field workflow's
	 *  Job Details panel builds its Directions link from all five. */
	projectAddressLine2?: string | null;
	projectCity?: string | null;
	projectState?: string | null;
	projectZip?: string | null;
	priority: 'low' | 'medium' | 'high' | 'urgent';
	scheduledDate: string;
	billingInfo: string[];
	billingName: string;
	billingAddress: string;
	billingNotes: string;
	billingAmount: number | null;
	/** Date the work was finished (work_orders.completion_date), stamped when
	 *  the record first reaches 'completed'. Survives the billing tail — an
	 *  invoiced or closed work order keeps the date it was completed on. */
	completionDate: string | null;
	/** Billing totals recalculated server-side on every update from the time
	 *  logs and the pay/tax rates. Decimal-as-string after API serialization,
	 *  like the estimate fields. Null until the job has both hours and a rate. */
	totalBillablePreTax: string | number | null;
	totalBillablePostTax: string | number | null;
	photosRequired: boolean;
	signatureRequired: boolean;
	/** URL of the captured on-site signature image. Null until the contact signs. */
	signatureData: string | null;
	photos: WorkOrderPhoto[];
	timeLogs: TimeLog[];
	/** Everyone assigned to the job (work_order_assignees pivot), eager-loaded
	 *  by GET /work-orders/{id}. The primary assignee is the row with
	 *  pivot.isPrimary — the rest are the additional crew. Optional because
	 *  the list endpoint doesn't load it. */
	assignees?: WorkOrderAssignee[];
	/** Free-text job notes shared by everyone working the job. Written from
	 *  the workflow's Job Details panel while the work is under way and from
	 *  the sign-off step at the end — one column, one set of notes. */
	checklistNotes?: string | null;
	/** Optional start time in UTC ISO 8601 format (e.g. "2024-03-15T14:00:00Z") */
	startTime: string | null;
	/** Optional end time in UTC ISO 8601 format */
	endTime: string | null;

	/** Single-point cost estimate (decimal-as-string after API serialization). */
	estimatedCost: string | number | null;
	/** Lower bound of optional cost range. */
	estimateMin: string | number | null;
	/** Upper bound of optional cost range. */
	estimateMax: string | number | null;
	/** Timestamp when the estimate was approved; null when unapproved. */
	estimateApprovedAt: string | null;
	/** Super Admin who approved the estimate (eager-loaded by /work-orders/{id}). */
	estimateApprovedByUser?: { id: string; firstName: string; lastName: string } | null;
	/** Customer-side contact who approved the estimate (eager-loaded). */
	estimateApprovedByContact?: { id: string; firstName: string; lastName: string } | null;

	/** Recurrence series ID — set when this WO was generated by a
	 *  Google-Calendar-style recurring schedule. Drives the
	 *  RecurrenceBadge on list rows and the detail-page header. */
	recurrenceSeriesId?: number | null;

	/** Parent Project Work Order — set when this WO is one child of a
	 *  project. The project's allocation panel then owns allocatedHours,
	 *  so the assignment surfaces hide their own hour bucket. */
	projectWorkOrderId?: number | string | null;

	/** Parent work order ID — set when this record is a Customer Service /
	 *  Follow-Up spawned from another work order. Null on regular WOs. */
	parentWorkOrderId: number | null;
	/** Parent inspection ID — set when this follow-up was spawned from a
	 *  failed inspection. Mutually exclusive with parentWorkOrderId. */
	parentInspectionId: number | null;
	/** Eager-loaded parent work order (project name only) for the
	 *  "Follow-up of …" banner on the detail page. */
	parentWorkOrder?: { id: number; projectName?: string; project_name?: string } | null;
	/** Eager-loaded parent inspection (with location.name) for the banner. */
	parentInspection?: { id: number; location?: { id: number; name: string } | null } | null;

	createdAt: string;
	updatedAt: string;
}

/**
 * Payload for creating a new work order.
 * Keys use camelCase (frontend convention) — the API client in api.ts automatically
 * converts them to snake_case before sending to the Laravel backend.
 * Status defaults to "pending" on the backend if not provided.
 *
 * Extends NotifyDecision: an admin's answer to the "Notify users" prompt
 * (notifyUsers / notifyEmails) travels on the save itself, since who gets
 * emailed is decided by the same request that triggers the emails. Omitted by
 * every other caller, which leaves the server's own audience rules in charge.
 */
export interface CreateWorkOrderData extends NotifyDecision {
	projectName: string;
	description: string;
	billingType: BillingType;
	businessLine: BusinessLineSlug;
	/** Optional CMS-managed Sub Type slug. */
	subType?: SubTypeSlug;
	/** "Require Hours" — opt-in hour tracking on any work order. Forced
	 *  true server-side when subType is T&M. */
	requiresHours?: boolean;
	/** Per-assignee hours map for hour-tracked jobs, keyed by user id.
	 *  Persists into the work_order_assignees pivot. */
	assigneeHours?: Record<string, number>;
	locationId: string;
	companyId: string;
	assigneeId?: string;
	/** Optional FK linking this WO to a parent Project Work Order. */
	projectWorkOrderId?: number | string;
	/** Bucket of hours this work order draws from. Doubles as the slice of
	 *  a parent project's hour pool when the WO is a project child — same
	 *  column, same meaning. Explicit null clears it in edit mode. */
	allocatedHours?: number | null;
	/** Optional array of additional technician user IDs to assign alongside the primary assignee.
	 *	These are stored in the work_order_assignees junction table on the backend. */
	additionalAssigneeIds?: string[];
	/** Contact snapshot trio — explicit null (edit mode) clears the stored value. */
	contactName?: string | null;
	contactPhone?: string | null;
	contactEmail?: string | null;
	/** FK to the selected Customer Contact. Explicit null (edit mode) unlinks
	 *  it — an absent key is ignored by the PATCH validator. */
	contactId?: number | null;
	projectAddress?: string;
	/** Address line 2 for the project site (suite, unit, floor, etc.) */
	projectAddressLine2?: string;
	priority?: 'low' | 'medium' | 'high' | 'urgent';
	/** Scheduled date (YYYY-MM-DD). Explicit null clears it in edit mode —
	 *  an absent key leaves the stored date alone. */
	scheduledDate?: string | null;
	/** Optional start time in UTC ISO 8601 format. Explicit null clears it. */
	startTime?: string | null;
	/** Optional end time in UTC ISO 8601 format. Explicit null clears it. */
	endTime?: string | null;
	billingInfo?: string[];
	billingName?: string;
	billingAddress?: string;
	/** Address line 2 for the billing address (suite, PO Box, dept, etc.) */
	billingAddressLine2?: string;
	/** City for the billing address */
	billingCity?: string;
	/** State for the billing address */
	billingState?: string;
	/** ZIP for the billing address */
	billingZip?: string;
	billingNotes?: string;
	photosRequired?: boolean;
	signatureRequired?: boolean;
	/** Parent work order — set when this record is a Customer Service /
	 *	Follow-Up spawned from another work order (warranty revisit). */
	parentWorkOrderId?: number;
	/** Parent inspection — set when this record is a follow-up spawned
	 *	from a failed inspection (one with at least one dissatisfied item). */
	parentInspectionId?: number;
}

/**
 * Payload for updating an existing work order.
 * All fields are optional (PATCH semantics) — only provided fields are updated.
 * Extends CreateWorkOrderData fields plus allows status transitions.
 */
export interface UpdateWorkOrderData extends Partial<CreateWorkOrderData> {
	status?: WorkOrderStatus;
	/** Field-captured job notes (work_orders.checklist_notes). Saved from the
	 *  workflow's Job Details panel so the crew can record what happened
	 *  on site without waiting for the sign-off step. */
	checklistNotes?: string | null;
}

/**
 * Payload for adding a time log entry to a work order.
 */
export interface CreateTimeLogData {
	employeeId: string;
	date: string;
	timeIn: string;
	timeOut: string;
	notes?: string;
}

/**
 * Payload for adding a photo to a work order.
 */
export interface CreatePhotoData {
	url: string;
	caption?: string;
	photoType: 'before' | 'after';
}

/**
 * Outcome of a bulk-invoice call. Both arrays are always present: `invoiced`
 * lists the ids that were billed, `skipped` the ones that were not, each with
 * a human-readable reason to show the user (already invoiced, not completed,
 * marked non-billable, hours not approved).
 */
export interface BulkInvoiceResult {
	invoiced: number[];
	skipped: { id: number; reason: string }[];
}

/**
 * Paginated API response wrapper for list endpoints.
 * Mirrors Laravel's pagination format with camelCase keys.
 */
export interface PaginatedResponse<T> {
	data: T[];
	current_page: number;
	last_page: number;
	per_page: number;
	total: number;
}

/* ============================================
 * RESPONSE NORMALIZATION
 * ============================================ */

/**
 * Flattens nested relation objects from the API response into the flat
 * properties expected by the WorkOrder interface.
 *
 * The backend returns eager-loaded relations as nested objects (e.g.,
 * { company: { name: "Acme" }, location: { name: "HQ" }, assignee: { firstName: "John", lastName: "Doe" } })
 * but the frontend interface expects flat properties (companyName, locationName, assigneeName).
 *
 * Call this AFTER camelizeKeys has converted all keys from snake_case to camelCase.
 *
 * @param raw - A camelized work order object with nested relation objects
 * @returns A normalized WorkOrder with flat relation fields
 */
export function normalizeWorkOrder(raw: Record<string, unknown>): WorkOrder {
	const company = raw.company as Record<string, unknown> | null;
	const location = raw.location as Record<string, unknown> | null;
	const assignee = raw.assignee as Record<string, unknown> | null;
	const contact = raw.contact as Record<string, unknown> | null;

	/* Resolve the primary assignee's user id from either the flat
	   (camelized) assignee_id or the nested assignee relation. */
	const assigneeIdRaw = (raw.assigneeId ?? assignee?.id) as string | number | null | undefined;
	/* Resolve the Customer Contact FK from the flat column or the nested
	   contact relation. */
	const contactIdRaw = (raw.contactId ?? contact?.id) as string | number | null | undefined;
	/* Resolve the on-site contact's linked user id. User links live in the
	   contact_users pivot, eager-loaded as contact.users on the detail
	   endpoint ("zero or one" in practice — first entry wins). Older flat
	   fallbacks are kept for defensive compatibility. */
	const contactUsers = (contact?.users ?? null) as Array<Record<string, unknown>> | null;
	const contactUserIdRaw = ((contactUsers?.[0]?.id ?? contact?.userId ?? raw.contactUserId) as string | number | null | undefined);

	return {
		...raw,
		/* Primary assignee user id — string-normalized for easy comparison. */
		assigneeId: assigneeIdRaw != null ? String(assigneeIdRaw) : null,
		/* Customer Contact FK (null when only the snapshot fields are set). */
		contactId: contactIdRaw != null ? String(contactIdRaw) : null,
		/* On-site contact's linked user id (null when the contact isn't a user). */
		contactUserId: contactUserIdRaw != null ? String(contactUserIdRaw) : null,
		/* Flatten company name from the nested company relation */
		companyName: (raw.companyName as string) || (company?.name as string) || '',
		/* Flatten location name from the nested location relation */
		locationName: (raw.locationName as string) || (location?.name as string) || '',
		/* Flatten assignee display name from the nested assignee relation */
		assigneeName: (raw.assigneeName as string) || (
			assignee
				? `${(assignee.firstName as string) || ''} ${(assignee.lastName as string) || ''}`.trim()
				: null
		),
		/* Flatten assignee role so the detail page can show a Subcontractor
		   badge without an extra fetch. Falls through to null when no
		   assignee is loaded. */
		assigneeRole: (raw.assigneeRole as string | null | undefined)
			?? (assignee?.role as string | null | undefined)
			?? null,
		/* Hour tracking. MySQL hands booleans back as 0/1, so coerce
		   rather than trusting the raw value in a truthiness check. The
		   T&M fall-through mirrors the server's own rule — a T&M job
		   tracks hours whether or not the stored flag says so, which keeps
		   records predating the flag rendering correctly. */
		requiresHours: Boolean(raw.requiresHours) || raw.subType === T_AND_M_SUB_TYPE,
	} as WorkOrder;
}

/* ============================================
 * SERVICE METHODS
 * ============================================ */

/**
 * Work order service methods.
 * Each method maps to a specific backend API endpoint.
 */
export const workOrderService = {
	/**
	 * GET /api/work-orders
	 * List work orders with optional query filters (status, company, search, etc.).
	 * Returns paginated results.
	 */
	getAll: (params?: Record<string, string>) =>
		api.get<PaginatedResponse<WorkOrder>>('/work-orders', params),

	/**
	 * GET /api/work-orders/:id
	 * Fetch a single work order by ID with all related data
	 * (photos, time logs, company, location, assignee).
	 */
	getById: (id: string) =>
		api.get<WorkOrder>(`/work-orders/${id}`),

	/**
	 * POST /api/work-orders
	 * Create a new work order. Returns the created resource with all relations.
	 */
	create: (data: CreateWorkOrderData) =>
		api.post<WorkOrder>('/work-orders', data),

	/**
	 * PATCH /api/work-orders/:id
	 * Partially update a work order (status changes, reassignment, billing, etc.).
	 */
	update: (id: string, data: UpdateWorkOrderData) =>
		api.patch<WorkOrder>(`/work-orders/${id}`, data),

	/**
	 * DELETE /api/work-orders/:id
	 * Soft-delete a work order (can be restored by admin).
	 */
	delete: (id: string) =>
		api.delete<void>(`/work-orders/${id}`),

	/**
	 * POST /api/work-orders/bulk-invoice
	 * Mark a batch of completed work orders as invoiced. Super Admin only —
	 * the whole billing tail (invoiced / closed) is office accounting.
	 *
	 * Partial success is the normal outcome, not an error: a row can go stale
	 * between the billing queue rendering and the button being pressed, so the
	 * server reports every id individually and still answers 200. Callers must
	 * read `skipped` rather than assuming a 200 billed everything they sent.
	 */
	bulkInvoice: (ids: string[]) =>
		api.post<BulkInvoiceResult>('/work-orders/bulk-invoice', { ids }),

	/**
	 * POST /api/work-orders/:id/time-logs
	 * Add a new time log entry to a work order. Returns the created time log.
	 */
	addTimeLog: (workOrderId: string, data: CreateTimeLogData) =>
		api.post<TimeLog>(`/work-orders/${workOrderId}/time-logs`, data),

	/**
	 * POST /api/work-orders/:id/photos
	 * Add a new photo to a work order. Returns the created photo record.
	 */
	addPhoto: (workOrderId: string, data: CreatePhotoData) =>
		api.post<WorkOrderPhoto>(`/work-orders/${workOrderId}/photos`, data),

	/**
	 * DELETE /api/work-orders/:id/photos/:photoId
	 * Remove a photo from a work order.
	 */
	deletePhoto: (workOrderId: string, photoId: string) =>
		api.delete<void>(`/work-orders/${workOrderId}/photos/${photoId}`),

	/**
	 * GET /api/work-orders/:id/pdf?type=internal|customer
	 * Generate a PDF report for the work order.
	 * Type 'internal' includes full details; 'customer' is a client-facing version.
	 * Returns the PDF as a downloadable blob URL.
	 */
	generatePdf: (id: string, type: 'internal' | 'customer') =>
		api.get<string>(`/work-orders/${id}/pdf`, { type }),
};
